/**
 * pft-product-design.js
 * Planet Friendly Threads — Product Design Page JS
 *
 * Handles:
 *  1. Section Rendering API swaps for selectors 1-5 (garment/cut/style/weight/colour)
 *  2. Variant selection for selector 6 (size)
 *  3. Thumbnail gallery image switching
 *  4. Add to Cart with AJAX and cart drawer integration
 *  5. Size guide modal open/close
 *  6. Zoom overlay for gallery (when enable_zoom is active)
 *
 * All logic uses event delegation on `document` to survive Section Rendering
 * HTML replacements without needing to rebind listeners.
 */

(function () {
  'use strict';

  /* -------------------------------------------------------------------------
     Utility helpers
     ---------------------------------------------------------------------- */

  /**
   * Read siblings JSON embedded by pft-product-info.liquid.
   * Returns parsed object or null.
   */
  function getSiblingsData() {
    const el = document.getElementById('pf-siblings-data');
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      console.warn('[PFT] Could not parse siblings data', e);
      return null;
    }
  }

  /**
   * Collect the currently selected selector values from the DOM.
   * Returns an object keyed by selector type.
   * e.g. { garment: 't-shirt', cut: 'unisex', style: 'short-sleeve', weight: '180gsm-medium-fit', colour: 'black' }
   */
  function getSelectedSelectors() {
    const selectors = ['garment', 'cut', 'style', 'weight', 'colour'];
    const result = {};
    selectors.forEach(function (key) {
      const checked = document.querySelector(
        '.pf-selector input[name="selector-' + key + '"]:checked'
      );
      if (checked) {
        result[key] = checked.value;
      }
    });
    return result;
  }

  /**
   * Given a sibling product object and the current selector state, determine
   * whether the sibling matches all provided selectors.
   * We match against the sibling's tags array.
   */
  function siblingMatchesSelectors(sibling, selectors) {
    const tags = sibling.tags || [];

    // Helper: does the tag array contain a tag with prefix+value?
    function hasTag(prefix, value) {
      const target = prefix + value;
      return tags.some(function (t) {
        return t.toLowerCase() === target.toLowerCase();
      });
    }

    if (selectors.garment && !hasTag('garment-', selectors.garment)) return false;
    if (selectors.cut && !hasTag('cut-', selectors.cut)) return false;
    if (selectors.style && !hasTag('style-', selectors.style)) return false;
    if (selectors.weight && !hasTag('weight-', selectors.weight)) return false;
    if (selectors.colour && !hasTag('colour-', selectors.colour)) return false;

    return true;
  }

  /**
   * Find the best matching sibling product for the current selector combination.
   * requiredKey: the selector dimension the user just changed — must match exactly.
   * Other dimensions are scored for best-effort fallback.
   */
  function findBestSibling(siblings, selectors, requiredKey) {
    if (!siblings || siblings.length === 0) return null;

    // If a requiredKey is given, filter to only siblings that match it.
    // This ensures "Hoodie" click always lands on a hoodie.
    var pool = siblings;
    if (requiredKey && selectors[requiredKey]) {
      var requiredTag = requiredKey + '-' + selectors[requiredKey];
      var filtered = siblings.filter(function (sib) {
        return (sib.tags || []).some(function (t) {
          return t.toLowerCase() === requiredTag.toLowerCase();
        });
      });
      if (filtered.length > 0) pool = filtered;
    }

    // Exact match within pool first
    for (var i = 0; i < pool.length; i++) {
      if (siblingMatchesSelectors(pool[i], selectors)) {
        return pool[i];
      }
    }

    // Score-based fallback within pool
    const keys = Object.keys(selectors).filter(function (k) {
      return selectors[k] && k !== requiredKey;
    });

    let bestSibling = null;
    let bestScore = -1;

    pool.forEach(function (sib) {
      const tags = sib.tags || [];
      let score = 0;
      keys.forEach(function (key) {
        const target = key + '-' + selectors[key];
        if (
          tags.some(function (t) {
            return t.toLowerCase() === target.toLowerCase();
          })
        ) {
          score++;
        }
      });
      if (score > bestScore) {
        bestScore = score;
        bestSibling = sib;
      }
    });

    return bestSibling;
  }

  /* -------------------------------------------------------------------------
     0. Populate selectors from sibling data on page load
     The Liquid only renders options from the current product's tags (1 per
     selector dimension). We need to discover additional options from siblings
     and inject them into the DOM so the user can switch between products.
     ---------------------------------------------------------------------- */

  /** Tag prefixes that map to each selector dimension */
  var SELECTOR_PREFIXES = {
    garment: 'garment-',
    cut: 'cut-',
    style: 'style-',
    weight: 'weight-',
    colour: 'colour-'
  };

  /** Colour name to hex map for swatch backgrounds */
  var COLOUR_HEX_MAP = {
    'white': '#FFFFFF', 'black': '#000000', 'navy': '#1B2A4A', 'french-navy': '#1B2A4A',
    'red': '#CC0000', 'blue': '#2B5EAF', 'grey': '#808080', 'gray': '#808080',
    'green': '#2E7D32', 'yellow': '#FFD600', 'orange': '#FF8200', 'pink': '#E91E63',
    'natural-raw': '#F5F0E8', 'heather-grey': '#B0B0B0', 'burgundy': '#800020',
    'khaki': '#BDB76B', 'desert-dust': '#C4A882', 'ocean-depth': '#0D4F4F'
  };

  function populateSelectorsFromSiblings() {
    var siblingsData = getSiblingsData();
    if (!siblingsData) return;

    var siblings = siblingsData.siblings || [];
    if (siblings.length === 0) return;

    // For each selector dimension, collect unique values from all siblings
    var selectorKeys = ['garment', 'cut', 'style', 'weight', 'colour'];

    selectorKeys.forEach(function (key) {
      var prefix = SELECTOR_PREFIXES[key];
      var fieldset = document.getElementById('pf-selector-' + key);
      if (!fieldset) return;

      var optionsContainer = fieldset.querySelector('.pf-selector__options');
      if (!optionsContainer) return;

      // Get values already present in the DOM
      var existingInputs = fieldset.querySelectorAll('input[name="selector-' + key + '"]');
      var existingValues = new Set();
      existingInputs.forEach(function (inp) { existingValues.add(inp.value); });

      // Collect new values from siblings
      var newValues = [];
      siblings.forEach(function (sib) {
        var tags = sib.tags || [];
        tags.forEach(function (tag) {
          if (typeof tag === 'string' && tag.indexOf(prefix) === 0) {
            var val = tag.substring(prefix.length);
            if (val && !existingValues.has(val) && newValues.indexOf(val) === -1) {
              newValues.push(val);
            }
          }
        });
      });

      // If we found new values, add them to the selector
      if (newValues.length > 0) {
        newValues.forEach(function (val) {
          var label = document.createElement('label');
          label.className = 'pf-selector__option' + (key === 'colour' ? ' pf-selector__option--swatch' : '');

          var input = document.createElement('input');
          input.type = 'radio';
          input.name = 'selector-' + key;
          input.value = val;
          input.dataset.selectorType = key;
          input.className = 'pf-sr-only';

          if (key === 'colour') {
            // Colour swatch
            var swatch = document.createElement('span');
            swatch.className = 'pf-swatch pf-swatch--lg';
            var hexColour = COLOUR_HEX_MAP[val] || val;
            swatch.style.backgroundColor = hexColour;
            swatch.setAttribute('aria-label', val.replace(/-/g, ' '));
            label.appendChild(input);
            label.appendChild(swatch);
          } else {
            // Standard pill
            var pill = document.createElement('span');
            pill.className = 'pf-pill';
            pill.setAttribute('role', 'none');
            // Format display text
            var displayText = val.replace(/-/g, ' ');
            if (key === 'weight') {
              displayText = displayText.toUpperCase();
            } else {
              displayText = displayText.charAt(0).toUpperCase() + displayText.slice(1);
            }
            pill.textContent = displayText;
            label.appendChild(input);
            label.appendChild(pill);
          }

          optionsContainer.appendChild(label);
        });

        // Show the selector now that it has 2+ options
        fieldset.style.display = '';
      }
    });
  }

  // Run on initial page load
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', populateSelectorsFromSiblings);
  } else {
    populateSelectorsFromSiblings();
  }

  // Re-run after Section Rendering swaps inject new HTML
  document.addEventListener('pft:product-swapped', function () {
    // Small delay to let the new DOM settle
    setTimeout(populateSelectorsFromSiblings, 50);
  });

  /* -------------------------------------------------------------------------
     1. Section Rendering API swaps (selectors 1-5)
     ---------------------------------------------------------------------- */

  let _swapInProgress = false;

  /**
   * Perform a Section Rendering API swap to the target product URL.
   * Fetches both gallery and info sections simultaneously.
   */
  function swapProductSections(targetUrl) {
    if (_swapInProgress) return;
    _swapInProgress = true;

    const galleryEl = document.getElementById('pf-product-gallery');
    const infoEl = document.getElementById('pf-product-info');

    // Visual feedback
    if (galleryEl) galleryEl.classList.add('pf-is-swapping');
    if (infoEl) infoEl.classList.add('pf-is-swapping');

    // Helper to remove loading state from current DOM elements by ID
    function clearSwappingClass() {
      const g = document.getElementById('pf-product-gallery');
      const i = document.getElementById('pf-product-info');
      if (g) g.classList.remove('pf-is-swapping');
      if (i) i.classList.remove('pf-is-swapping');
    }

    // Build section rendering URL.
    // Section IDs = file names without .liquid (pft-product-gallery, pft-product-info).
    // Preserve preview_theme_id so this works on draft/preview themes.
    const previewParam = new URLSearchParams(window.location.search).get('preview_theme_id');
    let fetchUrl = targetUrl + '?sections=pft-product-gallery,pft-product-info';
    if (previewParam) fetchUrl += '&preview_theme_id=' + encodeURIComponent(previewParam);

    // NOTE: Do NOT set Accept: application/json or X-Requested-With headers —
    // those cause Shopify to return product JSON instead of section HTML.
    fetch(fetchUrl)
      .then(function (response) {
        if (!response.ok) {
          throw new Error('Section Rendering fetch failed: ' + response.status);
        }
        return response.json();
      })
      .then(function (data) {
        // Keys match section file names (without .liquid).
        const newGalleryHtml = data['pft-product-gallery'];
        const newInfoHtml = data['pft-product-info'];

        if (!newGalleryHtml && !newInfoHtml) {
          // Section data missing — product may not use pft-design template.
          console.warn('[PFT] Section Rendering returned no section HTML. Ensure this product uses the pft-design template.', data);
          clearSwappingClass();
          return;
        }

        if (galleryEl && newGalleryHtml) {
          galleryEl.outerHTML = newGalleryHtml;
        }
        if (infoEl && newInfoHtml) {
          infoEl.outerHTML = newInfoHtml;
        }

        // Update browser URL without reload
        if (window.history && window.history.pushState) {
          window.history.pushState({ path: targetUrl }, '', targetUrl);
        }

        // Dispatch custom event for any other listeners (e.g. breadcrumbs, reviews)
        document.dispatchEvent(
          new CustomEvent('pft:product-swapped', {
            bubbles: true,
            detail: { url: targetUrl }
          })
        );
      })
      .catch(function (err) {
        console.error('[PFT] Section swap error:', err);
        clearSwappingClass();
      })
      .finally(function () {
        _swapInProgress = false;
      });
  }

  /**
   * Handle change events on selector radios 1-6.
   * Uses event delegation so it survives Section Rendering replacements.
   */
  document.addEventListener('change', function (event) {
    const input = event.target;

    // Guard: only handle radios inside non-size selector fieldsets
    if (
      input.tagName !== 'INPUT' ||
      input.type !== 'radio' ||
      !input.dataset.selectorType
    ) {
      return;
    }

    const selectorType = input.dataset.selectorType;

    // Selector 7 (size) is handled separately
    if (selectorType === 'size') return;

    const siblingsData = getSiblingsData();
    if (!siblingsData) {
      console.warn('[PFT] No siblings data found — cannot swap product.');
      return;
    }

    const currentSelectors = getSelectedSelectors();
    const siblings = siblingsData.siblings || [];

    const match = findBestSibling(siblings, currentSelectors, selectorType);
    if (!match) {
      console.info('[PFT] No matching sibling found for selectors:', currentSelectors);
      return;
    }

    swapProductSections(match.url);
  });

  /* -------------------------------------------------------------------------
     2. Variant selection — selector 6 (size)
     ---------------------------------------------------------------------- */

  document.addEventListener('change', function (event) {
    const input = event.target;

    if (
      input.tagName !== 'INPUT' ||
      input.type !== 'radio' ||
      input.name !== 'variant-id'
    ) {
      return;
    }

    const variantId = input.value;
    const variantPrice = input.dataset.variantPrice;
    const variantComparePrice = input.dataset.variantComparePrice;
    const variantAvailable = input.dataset.variantAvailable === 'true';

    // Update hidden variant ID input in the ATC form
    const variantInput = document.getElementById('pf-variant-id');
    if (variantInput) {
      variantInput.value = variantId;
    }

    // Update price display
    const priceEl = document.getElementById('pf-price-display');
    if (priceEl && variantPrice) {
      const price = parseInt(variantPrice, 10);
      const comparePrice = variantComparePrice ? parseInt(variantComparePrice, 10) : 0;
      const formatted = formatMoney(price);
      const formattedCompare = comparePrice > price ? formatMoney(comparePrice) : '';

      let priceHtml = '';
      if (formattedCompare) {
        priceHtml +=
          '<s class="pf-product-info__price-compare" aria-label="Original price ' +
          escapeHtml(formattedCompare) +
          '">' +
          escapeHtml(formattedCompare) +
          '</s>';
        priceHtml +=
          '<span class="pf-product-info__price-sale">' +
          escapeHtml(formatted) +
          '</span>';
        priceHtml +=
          '<span class="pf-product-info__price-badge">Sale</span>';
      } else {
        priceHtml =
          '<span class="pf-product-info__price-current">' +
          escapeHtml(formatted) +
          '</span>';
      }

      if (!variantAvailable) {
        priceHtml +=
          '<span class="pf-product-info__sold-out-badge">Sold Out</span>';
      }

      priceEl.innerHTML = priceHtml;
    }

    // Update ATC button state
    const atcBtn = document.getElementById('pf-atc-btn');
    if (atcBtn) {
      const textEl = atcBtn.querySelector('.pf-atc-btn__text');
      if (variantAvailable) {
        atcBtn.disabled = false;
        atcBtn.removeAttribute('aria-disabled');
        if (textEl) textEl.textContent = 'Add to Bag';
      } else {
        atcBtn.disabled = true;
        atcBtn.setAttribute('aria-disabled', 'true');
        if (textEl) textEl.textContent = 'Sold Out';
      }
    }
  });

  /* -------------------------------------------------------------------------
     3. Thumbnail gallery
     ---------------------------------------------------------------------- */

  document.addEventListener('click', function (event) {
    const thumb = event.target.closest('.pf-gallery__thumb');
    if (!thumb) return;

    const imageSrc = thumb.dataset.imageSrc;
    const imageSrcset = thumb.dataset.imageSrcset;
    const imageAlt = thumb.dataset.imageAlt || '';

    // Update main image
    const mainImg = document.getElementById('pf-gallery-main-img');
    if (mainImg && imageSrc) {
      mainImg.src = imageSrc;
      if (imageSrcset) mainImg.srcset = imageSrcset;
      mainImg.alt = imageAlt;
    }

    // Toggle active class and aria-pressed
    const allThumbs = document.querySelectorAll('.pf-gallery__thumb');
    allThumbs.forEach(function (t) {
      t.classList.remove('is-active');
      t.setAttribute('aria-pressed', 'false');
    });
    thumb.classList.add('is-active');
    thumb.setAttribute('aria-pressed', 'true');
  });

  /* -------------------------------------------------------------------------
     4. Add to Cart — AJAX with cart drawer integration
     ---------------------------------------------------------------------- */

  document.addEventListener('submit', function (event) {
    const form = event.target;
    if (form.id !== 'pf-product-form') return;

    event.preventDefault();

    const atcBtn = document.getElementById('pf-atc-btn');
    const spinner = document.getElementById('pf-atc-spinner');
    const btnText = atcBtn ? atcBtn.querySelector('.pf-atc-btn__text') : null;
    const errorEl = document.getElementById('pf-atc-error');
    const variantInput = document.getElementById('pf-variant-id');

    if (!variantInput || !variantInput.value) {
      showAtcError('Please select a size before adding to bag.');
      return;
    }

    // Show loading state
    if (atcBtn) {
      atcBtn.disabled = true;
      atcBtn.setAttribute('aria-busy', 'true');
    }
    if (spinner) spinner.style.display = '';
    if (btnText) btnText.style.display = 'none';
    if (errorEl) errorEl.style.display = 'none';

    const formData = new FormData(form);

    fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: formData
    })
      .then(function (response) {
        return response.json().then(function (data) {
          if (!response.ok) {
            throw new Error(
              (data && data.description) ||
                (data && data.message) ||
                'Could not add item to cart.'
            );
          }
          return data;
        });
      })
      .then(function (cartItem) {
        // Restore button
        restoreAtcButton(true);

        // Notify cart
        openCartDrawer(cartItem);

        // Dispatch event for Dawn's cart bubble / other listeners
        document.dispatchEvent(
          new CustomEvent('cart:refresh', {
            bubbles: true,
            detail: { cartItem: cartItem }
          })
        );
      })
      .catch(function (err) {
        restoreAtcButton(false);
        const message =
          err && err.message ? err.message : 'Something went wrong. Please try again.';
        showAtcError(message);
        console.error('[PFT] Add to cart error:', err);
      });

    function restoreAtcButton(success) {
      if (atcBtn) {
        atcBtn.disabled = false;
        atcBtn.removeAttribute('aria-busy');
      }
      if (spinner) spinner.style.display = 'none';
      if (btnText) {
        btnText.style.display = '';
        if (success) {
          btnText.textContent = 'Added!';
          // Reset after 2s
          setTimeout(function () {
            btnText.textContent = 'Add to Bag';
          }, 2000);
        }
      }
    }

    function showAtcError(message) {
      if (errorEl) {
        errorEl.textContent = message;
        errorEl.style.display = '';
        errorEl.focus();
      }
    }
  });

  /**
   * Open Dawn's cart drawer or fall back to navigating to /cart.
   */
  function openCartDrawer(cartItem) {
    // Dawn's cart drawer listens for 'cart:refresh'
    // Also try direct DOM API used by some Dawn versions
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer && typeof cartDrawer.open === 'function') {
      cartDrawer.open();
      return;
    }

    // Dawn 15+ dispatches on window
    if (window.Shopify && window.Shopify.theme && window.Shopify.theme.cartDrawer) {
      try {
        window.Shopify.theme.cartDrawer.open();
        return;
      } catch (e) {
        // noop — will fall through
      }
    }

    // Last resort: dispatch the event Dawn uses internally
    document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    window.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true }));
  }

  /* -------------------------------------------------------------------------
     5. Size guide modal
     ---------------------------------------------------------------------- */

  // Open
  document.addEventListener('click', function (event) {
    const btn = event.target.closest('.pf-size-guide-link');
    if (!btn) return;

    const modalId = btn.getAttribute('aria-controls');
    if (!modalId) return;

    const modal = document.getElementById(modalId);
    if (!modal) return;

    openSizeGuideModal(modal, btn);
  });

  // Close via close button or overlay
  document.addEventListener('click', function (event) {
    // Close button
    if (event.target.closest('.pf-size-guide-modal__close')) {
      const modal = event.target.closest('.pf-size-guide-modal');
      if (modal) closeSizeGuideModal(modal);
      return;
    }

    // Overlay click (clicking directly on the modal backdrop, not the dialog)
    const modal = event.target.closest('.pf-size-guide-modal');
    if (modal && event.target === modal) {
      closeSizeGuideModal(modal);
    }
  });

  // Close on Escape key
  document.addEventListener('keydown', function (event) {
    if (event.key !== 'Escape') return;
    const openModal = document.querySelector('.pf-size-guide-modal[aria-hidden="false"]');
    if (openModal) {
      closeSizeGuideModal(openModal);
    }
  });

  function openSizeGuideModal(modal, trigger) {
    modal.setAttribute('aria-hidden', 'false');
    modal.style.display = '';

    if (trigger) {
      trigger.setAttribute('aria-expanded', 'true');
      modal._trigger = trigger;
    }

    // Focus the close button
    const closeBtn = modal.querySelector('.pf-size-guide-modal__close');
    if (closeBtn) {
      setTimeout(function () {
        closeBtn.focus();
      }, 50);
    }

    // Prevent body scroll
    document.body.style.overflow = 'hidden';

    // Trap focus within modal
    trapFocus(modal);
  }

  function closeSizeGuideModal(modal) {
    modal.setAttribute('aria-hidden', 'true');

    // Return focus to trigger
    if (modal._trigger) {
      modal._trigger.setAttribute('aria-expanded', 'false');
      modal._trigger.focus();
      modal._trigger = null;
    }

    // Restore scroll
    document.body.style.overflow = '';
  }

  /* -------------------------------------------------------------------------
     6. Zoom overlay
     ---------------------------------------------------------------------- */

  // Open zoom on main image click
  document.addEventListener('click', function (event) {
    const gallery = event.target.closest('.pf-gallery--zoomable');
    if (!gallery) return;

    const mainImg = event.target.closest('#pf-gallery-main img');
    if (!mainImg) return;

    const overlay = document.getElementById('pf-gallery-zoom');
    const zoomImg = document.getElementById('pf-gallery-zoom-img');
    if (!overlay || !zoomImg) return;

    zoomImg.src = mainImg.src;
    zoomImg.alt = mainImg.alt;

    overlay.style.display = '';
    overlay.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';

    const closeBtn = overlay.querySelector('.pf-gallery__zoom-close');
    if (closeBtn) {
      setTimeout(function () {
        closeBtn.focus();
      }, 50);
    }
  });

  // Close zoom
  document.addEventListener('click', function (event) {
    if (event.target.closest('.pf-gallery__zoom-close')) {
      closeZoom();
      return;
    }

    const overlay = event.target.closest('#pf-gallery-zoom');
    if (overlay && !event.target.closest('#pf-gallery-zoom img')) {
      closeZoom();
    }
  });

  document.addEventListener('keydown', function (event) {
    if (event.key !== 'Escape') return;
    const overlay = document.getElementById('pf-gallery-zoom');
    if (overlay && overlay.getAttribute('aria-hidden') === 'false') {
      closeZoom();
    }
  });

  function closeZoom() {
    const overlay = document.getElementById('pf-gallery-zoom');
    if (!overlay) return;
    overlay.setAttribute('aria-hidden', 'true');
    overlay.style.display = 'none';
    document.body.style.overflow = '';

    // Return focus to main image area
    const mainContainer = document.getElementById('pf-gallery-main');
    if (mainContainer) mainContainer.focus();
  }

  /* -------------------------------------------------------------------------
     Focus trap utility (for modals)
     ---------------------------------------------------------------------- */

  function trapFocus(container) {
    const focusableSelectors = [
      'a[href]',
      'area[href]',
      'input:not([disabled])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      'button:not([disabled])',
      '[tabindex]:not([tabindex="-1"])'
    ].join(', ');

    const focusableEls = Array.from(container.querySelectorAll(focusableSelectors));
    if (focusableEls.length === 0) return;

    const firstEl = focusableEls[0];
    const lastEl = focusableEls[focusableEls.length - 1];

    function handleKeydown(e) {
      if (e.key !== 'Tab') return;

      if (e.shiftKey) {
        if (document.activeElement === firstEl) {
          e.preventDefault();
          lastEl.focus();
        }
      } else {
        if (document.activeElement === lastEl) {
          e.preventDefault();
          firstEl.focus();
        }
      }
    }

    // Store so we can remove on close
    container._trapKeydown = handleKeydown;
    container.addEventListener('keydown', handleKeydown);

    // Clean up when modal closes
    const observer = new MutationObserver(function (mutations) {
      mutations.forEach(function (m) {
        if (
          m.type === 'attributes' &&
          m.attributeName === 'aria-hidden' &&
          container.getAttribute('aria-hidden') === 'true'
        ) {
          container.removeEventListener('keydown', handleKeydown);
          observer.disconnect();
        }
      });
    });
    observer.observe(container, { attributes: true });
  }

  /* -------------------------------------------------------------------------
     Money formatting utility
     Uses Shopify's global money format if available, otherwise formats manually.
     ---------------------------------------------------------------------- */

  function formatMoney(cents) {
    if (
      window.Shopify &&
      window.Shopify.formatMoney
    ) {
      return window.Shopify.formatMoney(cents);
    }

    // Fallback: format as currency with 2 decimal places
    const amount = (cents / 100).toFixed(2);
    // Try to get currency symbol from existing price elements
    const existingPrice = document.querySelector('.pf-product-info__price-current, .pf-product-info__price-sale');
    if (existingPrice) {
      const text = existingPrice.textContent.trim();
      const currencyMatch = text.match(/^([^0-9\s]+)/);
      const symbol = currencyMatch ? currencyMatch[1] : '\u20ac';
      return symbol + amount;
    }
    return '\u20ac' + amount;
  }

  /**
   * Escape HTML special characters for safe insertion into innerHTML.
   */
  function escapeHtml(str) {
    if (!str) return '';
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  /* -------------------------------------------------------------------------
     Handle browser back/forward navigation (popstate)
     If the user navigates back to a product page URL after a Section Rendering
     swap, we reload the page so the correct product is displayed.
     ---------------------------------------------------------------------- */

  window.addEventListener('popstate', function (event) {
    // Only reload if we're on a product page
    if (window.location.pathname.includes('/products/')) {
      window.location.reload();
    }
  });

  /* -------------------------------------------------------------------------
     Accordion initialisation
     Accordion triggers are rendered server-side; JS handles toggle behaviour.
     ---------------------------------------------------------------------- */

  document.addEventListener('click', function (event) {
    const trigger = event.target.closest('.pf-accordion__trigger');
    if (!trigger) return;

    const expanded = trigger.getAttribute('aria-expanded') === 'true';
    const panelId = trigger.getAttribute('aria-controls');
    const panel = panelId ? document.getElementById(panelId) : null;

    if (!panel) return;

    // Close all other open accordion items in the same accordion group
    const accordion = trigger.closest('.pf-accordion');
    if (accordion) {
      const allTriggers = accordion.querySelectorAll('.pf-accordion__trigger');
      allTriggers.forEach(function (t) {
        if (t === trigger) return;
        t.setAttribute('aria-expanded', 'false');
        const pid = t.getAttribute('aria-controls');
        const p = pid ? document.getElementById(pid) : null;
        if (p) {
          p.setAttribute('aria-hidden', 'true');
          p.classList.remove('is-open');
        }
      });
    }

    // Toggle current item
    const newExpanded = !expanded;
    trigger.setAttribute('aria-expanded', String(newExpanded));
    panel.setAttribute('aria-hidden', String(!newExpanded));
    if (newExpanded) {
      panel.classList.add('is-open');
    } else {
      panel.classList.remove('is-open');
    }
  });

  /* -------------------------------------------------------------------------
     Re-hydrate after Section Rendering swap
     After pft:product-swapped fires, the new HTML is in place. Any
     components that require JS initialisation (lazy images, etc.) are
     re-triggered here.
     ---------------------------------------------------------------------- */

  document.addEventListener('pft:product-swapped', function () {
    // Trigger lazy image loading for newly inserted thumbnails
    if ('IntersectionObserver' in window) {
      initLazyImages();
    }
  });

  function initLazyImages() {
    const lazyImgs = document.querySelectorAll('img[data-src]');
    if (!lazyImgs.length) return;

    const observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          const img = entry.target;
          if (img.dataset.src) img.src = img.dataset.src;
          if (img.dataset.srcset) img.srcset = img.dataset.srcset;
          img.classList.add('is-loaded');
          observer.unobserve(img);
        });
      },
      { rootMargin: '200px' }
    );

    lazyImgs.forEach(function (img) {
      observer.observe(img);
    });
  }

  // Run on initial load too
  if ('IntersectionObserver' in window) {
    initLazyImages();
  }

})();
